import { ReactNode } from "react";
import { SupportEmail, TelegramBotLink } from "../../common/help";

export type FaqCtaKind = "telegram" | "site";

export interface FaqCta {
  label: string;
  kind: FaqCtaKind;
}

export interface FaqQuestion {
  question: string;
  answer: ReactNode;
  cta?: FaqCta[];
}

export interface FaqCategory {
  slug: string;
  title: string;
  questions: FaqQuestion[];
  note?: ReactNode;
  footerCta?: "telegram";
}

const TelegramLink = ({ children }: { children: ReactNode }) => (
  <a href={TelegramBotLink} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const SupportEmailLink = () => (
  <a href={`mailto:${SupportEmail}`}>{SupportEmail}</a>
);

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    slug: "getting-started",
    title: "Начало работы",
    questions: [
      {
        question: "Что такое OpenWay?",
        answer: (
          <p>
            OpenWay – это сервис для безопасного доступа в интернет, который помогает защитить
            соединение, сохранить конфиденциальность и получить доступ к необходимым
            онлайн-ресурсам.
          </p>
        ),
      },
      {
        question: "Как начать пользоваться?",
        answer: (
          <ol>
            <li>
              Откройте приложение в <TelegramLink>Telegram-боте</TelegramLink> или авторизуйтесь
              на сайте
            </li>
            <li>Оформите подписку</li>
            <li>Нажмите «Добавить устройство» на главном экране и следуйте инструкции</li>
            <li>
              Привяжите email, если оформляли подписку через бота, или telegram, если оформляли
              подписку через сайт
            </li>
          </ol>
        ),
        cta: [
          { label: "Перейти в Telegram-бот", kind: "telegram" },
          { label: "Перейти на сайт", kind: "site" },
        ],
      },
      {
        question: "Нужен ли телеграм для использования?",
        answer: (
          <p>
            Нет, вы можете управлять подпиской и входить в личный кабинет через сайт с помощью
            Email.
          </p>
        ),
        cta: [{ label: "Перейти на сайт", kind: "site" }],
      },
    ],
    note: (
      <p>
        Если вы уже использовали Telegram-бот, не удаляйте и не блокируйте его. Бот уведомляет
        вас об окончании подписки и предупреждает о технических работах.
      </p>
    ),
  },
  {
    slug: "subscription-payment",
    title: "Подписка и оплата",
    questions: [
      {
        question: "Какие тарифы доступны?",
        answer: (
          <>
            <p>Мы предлагаем тарифы на:</p>
            <ul>
              <li>3 устройства</li>
              <li>5 устройств</li>
              <li>10 устройств</li>
            </ul>
          </>
        ),
      },
      {
        question: "Можно ли сменить тариф?",
        answer: (
          <>
            <p>
              Да, вы можете перейти на тариф с другим количеством устройств в любой момент через
              личный кабинет. Стоимость перехода на новый тариф считается в зависимости от
              оставшегося периода подписки.
            </p>
            <p>Минимальная стоимость перехода на новый тариф – 100 рублей.</p>
          </>
        ),
      },
      {
        question: "Что будет после окончания подписки?",
        answer: (
          <>
            <p>Доступ автоматически приостановится до продления подписки.</p>
            <p>
              Чтобы не потерять доступ, советуем оплачивать продление подписки заранее в личном
              кабинете. Telegram-бот уведомит вас за 3 дня до её окончания.
            </p>
          </>
        ),
      },
      {
        question: "Можно ли вернуть деньги?",
        answer: (
          <p>
            Если возникли технические проблемы или вы оформили подписку по ошибке, свяжитесь с
            нами в <TelegramLink>Telegram</TelegramLink> или отправьте письмо на{" "}
            <SupportEmailLink /> для рассмотрения ситуации.
          </p>
        ),
      },
    ],
    footerCta: "telegram",
  },
  {
    slug: "devices-connection",
    title: "Устройства и подключение",
    questions: [
      {
        question: "На каких устройствах работает OpenWay?",
        answer: (
          <>
            <p>Поддерживаются:</p>
            <ul>
              <li>Windows</li>
              <li>macOS</li>
              <li>Android</li>
              <li>iPhone и iPad</li>
              <li>Linux</li>
              <li>Android TV</li>
            </ul>
          </>
        ),
      },
      {
        question: "Можно ли использовать подписку на нескольких устройствах?",
        answer: (
          <p>
            Да. Количество одновременно подключенных устройств зависит от выбранного тарифа.
          </p>
        ),
      },
      {
        question: "Как добавить новое устройство?",
        answer: (
          <p>
            На главном экране приложения или сайта нажмите кнопку «Добавить устройство».
            Выберите нужное устройство и следуйте инструкциям по подключению.
          </p>
        ),
      },
      {
        question: "Что делать, если закончился лимит устройств?",
        answer: (
          <>
            <p>
              Докупите количество устройств, нажав кнопку «Продлить подписку» на главном экране
              личного кабинета в приложении или на сайте.
            </p>
            <p>
              Или освободите место в разделе «Управление устройствами». Для этого удалите те
              устройства, которые хотите заменить новыми.
            </p>
          </>
        ),
      },
    ],
  },
  {
    slug: "vpn-operation",
    title: "Работа VPN",
    questions: [
      {
        question: "Почему скорость через VPN ниже обычной скорости интернета?",
        answer: (
          <p>
            VPN дополнительно шифрует трафик, поэтому скорость может немного снижаться. Обычно
            разница составляет 5–20%.
          </p>
        ),
      },
      {
        question: "Какие серверы доступны?",
        answer: (
          <>
            <p>В настоящий момент доступны:</p>
            <ul>
              <li>Финляндия</li>
              <li>Германия</li>
              <li>Россия</li>
              <li>Нидерланды</li>
            </ul>
            <p>Список регулярно пополняется новыми локациями.</p>
          </>
        ),
      },
      {
        question: "Можно ли использовать VPN для игр?",
        answer: (
          <p>
            Мы не рекомендуем использовать VPN для онлайн-игр. Пинг может отличаться в
            зависимости от выбранного сервера и маршрута подключения.
          </p>
        ),
      },
    ],
  },
  {
    slug: "whitelist-bypass",
    title: "Обход белых списков",
    questions: [
      {
        question: "Что такое режим обхода белых списков?",
        answer: (
          <p>
            Это специальный тип подключения, разработанный специально для работы в районах с
            введенным режимом белых списков.
          </p>
        ),
      },
      {
        question: "Чем он отличается от обычного VPN?",
        answer: (
          <p>
            Он использует дополнительные методы маскировки трафика, что помогает поддерживать
            стабильное соединение при введенном режиме белых списков.
          </p>
        ),
      },
      {
        question: "Почему обход работает не всегда одинаково?",
        answer: (
          <p>
            Методы блокировки постоянно меняются, поэтому эффективность может зависеть от
            региона, провайдера и времени суток.
          </p>
        ),
      },
    ],
  },
  {
    slug: "technical-issues",
    title: "Технические проблемы",
    questions: [
      {
        question: "Не получается подключиться, что делать?",
        answer: (
          <>
            <p>Попробуйте:</p>
            <ul>
              <li>Сменить сервер</li>
              <li>
                Обновить подписку в приложении Happ (или любом другом клиенте, который
                используете), нажав «...», затем «🔄 Обновить подписку» в открывшемся меню
              </li>
              <li>Включить/Выключить режим полета на устройстве</li>
              <li>Перезагрузить устройство</li>
            </ul>
            <p>
              Если вышеперечисленные варианты не помогли, свяжитесь с нами в{" "}
              <TelegramLink>Telegram</TelegramLink> или отправьте письмо на <SupportEmailLink />.
            </p>
          </>
        ),
      },
    ],
    footerCta: "telegram",
  },
  {
    slug: "account-linking",
    title: "Привязка аккаунта",
    questions: [
      {
        question: "Как привязать Telegram-аккаунт к сайту?",
        answer: (
          <>
            <p>Чтобы привязать ваш Telegram к подписке на сайте, необходимо:</p>
            <ol>
              <li>Авторизоваться на сайте под вашей почтой</li>
              <li>Перейти в «Мой профиль»</li>
              <li>Выбрать «Способы входа»</li>
              <li>Telegram «Привязать»</li>
              <li>Когда откроется Telegram-бот, подтвердите действие в чате, выбрав «Да, привязать»</li>
            </ol>
            <p>
              Готово, теперь вы можете управлять своей подпиской через Telegram и личный кабинет
              на сайте.
            </p>
          </>
        ),
        cta: [{ label: "Перейти на сайт", kind: "site" }],
      },
      {
        question: "Как привязать email к личному кабинету в Telegram?",
        answer: (
          <>
            <p>Чтобы привязать вашу почту к подписке в Telegram-боте, необходимо:</p>
            <ol>
              <li>Открыть приложение в Telegram-боте</li>
              <li>Перейти в «Мой профиль»</li>
              <li>Выбрать «Способы входа»</li>
              <li>Email «Привязать»</li>
              <li>Указать почту и ввести код подтверждения</li>
            </ol>
            <p>
              Готово, теперь вы можете управлять своей подпиской через Telegram и личный кабинет
              на сайте.
            </p>
          </>
        ),
        cta: [{ label: "Перейти в Telegram-бот", kind: "telegram" }],
      },
    ],
  },
];

export const getFaqCategory = (slug: string | undefined): FaqCategory | undefined =>
  FAQ_CATEGORIES.find((category) => category.slug === slug);
